import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  LinearProgress,
  Stack,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  ListItemIcon,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Breadcrumbs,
  Link,
  CircularProgress,
} from "@mui/material";
import { Add as AddIcon, Add, Delete as DeleteIcon, FolderOutlined, NavigateNext, ArrowBack, PersonAdd } from "@mui/icons-material";
import { useMutation, useAction } from "convex/react";
import { useState } from "react";
import { useMsal } from "@azure/msal-react";
import { useStableQuery } from "../hooks/useStableQuery";
import { api } from "../../../convex/_generated/api";
import { QBConnectionCard } from "../components/qb-desktop/QBConnectionCard";
import { AgentAvatar } from "../components/AgentAvatar";
import { agentColor, splitAgentName, tint } from "../utils/agentDisplay";
import { QBCommandQueue } from "../components/qb-desktop/QBCommandQueue";

function timeAgo(timestamp: number): string {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function Settings() {
  const { accounts } = useMsal();
  const email = accounts[0]?.username ?? "";
  const userRecord = useStableQuery(api.users.getByEmail, email ? { email } : "skip", null);
  const role = userRecord?.role ?? "viewer";
  const isAdmin = role === "admin";

  const agentStatuses = useStableQuery(api.agentStatus.getAll, {}, []);
  const emailCollectionStatus = useStableQuery(api.emailCollection.getStatus, {}, null);

  // SharePoint folder management
  const scanFolders = useStableQuery(api.scanConfig.listFolders, {}, []);
  const addFolder = useMutation(api.scanConfig.addFolder);
  const removeFolder = useMutation(api.scanConfig.removeFolder);
  const toggleFolder = useMutation(api.scanConfig.toggleFolder);

  // SharePoint discovery actions
  const resolveSiteAction = useAction(api.scanConfig.resolveSite);
  const listDrivesAction = useAction(api.scanConfig.listDrives);
  const browseFoldersAction = useAction(api.scanConfig.browseFolders);

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newFolder, setNewFolder] = useState({
    siteId: "",
    folderId: "",
    folderPath: "",
    fileTypes: ["pdf", "csv", "ofx"] as string[],
  });
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  // User management
  const allUsers = useStableQuery(api.users.list, isAdmin ? {} : "skip", []);
  const upsertUser = useMutation(api.users.upsert);
  const removeUser = useMutation(api.users.remove);
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserName, setNewUserName] = useState("");
  const [newUserRole, setNewUserRole] = useState<"admin" | "accountant" | "viewer">("viewer");
  const [deleteUserId, setDeleteUserId] = useState<string | null>(null);

  // Wizard state
  const [wizardStep, setWizardStep] = useState<1 | 2 | 3>(1);
  const [siteUrl, setSiteUrl] = useState("");
  const [siteInfo, setSiteInfo] = useState<{ siteId: string; displayName: string } | null>(null);
  const [drives, setDrives] = useState<Array<{ driveId: string; name: string }>>([]);
  const [selectedDrive, setSelectedDrive] = useState<{ driveId: string; name: string } | null>(null);
  const [folders, setFolders] = useState<Array<{ id: string; name: string; path: string; childCount: number }>>([]);
  const [breadcrumb, setBreadcrumb] = useState<Array<{ id: string | null; name: string }>>([]);
  const [loading, setLoading] = useState(false);
  const [wizardError, setWizardError] = useState<string | null>(null);
  const [selectedFileTypes, setSelectedFileTypes] = useState<string[]>(["pdf", "csv", "ofx"]);

  const resetWizard = () => {
    setWizardStep(1);
    setSiteUrl("");
    setSiteInfo(null);
    setDrives([]);
    setSelectedDrive(null);
    setFolders([]);
    setBreadcrumb([]);
    setLoading(false);
    setWizardError(null);
    setSelectedFileTypes(["pdf", "csv", "ofx"]);
  };

  const handleCloseAddDialog = () => {
    setAddDialogOpen(false);
    resetWizard();
  };

  const handleResolveSite = async () => {
    if (!siteUrl.trim()) return;
    setLoading(true);
    setWizardError(null);
    try {
      const result = await resolveSiteAction({ url: siteUrl.trim() });
      if ("error" in result) {
        setWizardError(result.error as string);
      } else {
        setSiteInfo({ siteId: result.siteId as string, displayName: result.displayName as string });
        // Immediately load drives
        const drivesResult = await listDrivesAction({ siteId: result.siteId as string });
        if ("error" in drivesResult) {
          setWizardError(drivesResult.error as string);
        } else {
          setDrives(drivesResult as Array<{ driveId: string; name: string }>);
          setWizardStep(2);
        }
      }
    } catch (err) {
      setWizardError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  const handleSelectDrive = async (drive: { driveId: string; name: string }) => {
    setSelectedDrive(drive);
    setLoading(true);
    setWizardError(null);
    try {
      const result = await browseFoldersAction({
        siteId: siteInfo!.siteId,
        driveId: drive.driveId,
      });
      if ("error" in result) {
        setWizardError(result.error as string);
      } else {
        setFolders(result as Array<{ id: string; name: string; path: string; childCount: number }>);
        setBreadcrumb([{ id: null, name: drive.name }]);
        setWizardStep(3);
      }
    } catch (err) {
      setWizardError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  const handleNavigateFolder = async (folderId: string, folderName: string) => {
    setLoading(true);
    setWizardError(null);
    try {
      const result = await browseFoldersAction({
        siteId: siteInfo!.siteId,
        driveId: selectedDrive!.driveId,
        folderId,
      });
      if ("error" in result) {
        setWizardError(result.error as string);
      } else {
        setFolders(result as Array<{ id: string; name: string; path: string; childCount: number }>);
        setBreadcrumb((prev) => [...prev, { id: folderId, name: folderName }]);
      }
    } catch (err) {
      setWizardError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  const handleBreadcrumbClick = async (index: number) => {
    if (index === breadcrumb.length - 1) return; // Already here
    setLoading(true);
    setWizardError(null);
    const target = breadcrumb[index];
    try {
      const result = await browseFoldersAction({
        siteId: siteInfo!.siteId,
        driveId: selectedDrive!.driveId,
        ...(target.id ? { folderId: target.id } : {}),
      });
      if ("error" in result) {
        setWizardError(result.error as string);
      } else {
        setFolders(result as Array<{ id: string; name: string; path: string; childCount: number }>);
        setBreadcrumb((prev) => prev.slice(0, index + 1));
      }
    } catch (err) {
      setWizardError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  const handleSelectThisFolder = async () => {
    const currentFolderId =
      breadcrumb.length > 1
        ? breadcrumb[breadcrumb.length - 1].id!
        : `root:${selectedDrive!.driveId}`;
    const folderPathStr = [siteInfo!.displayName, ...breadcrumb.map((b) => b.name)].join(" / ");

    await addFolder({
      siteId: siteInfo!.siteId,
      folderId: currentFolderId,
      folderPath: folderPathStr,
      fileTypes: selectedFileTypes,
    });
    handleCloseAddDialog();
  };

  const handleAddFolder = async () => {
    if (!newFolder.siteId || !newFolder.folderId || !newFolder.folderPath) return;
    await addFolder({
      siteId: newFolder.siteId,
      folderId: newFolder.folderId,
      folderPath: newFolder.folderPath,
      fileTypes: newFolder.fileTypes,
    });
    setNewFolder({ siteId: "", folderId: "", folderPath: "", fileTypes: ["pdf", "csv", "ofx"] });
    setAddDialogOpen(false);
  };

  const handleRemoveFolder = async (id: string) => {
    await removeFolder({ id: id as any });
    setDeleteConfirmId(null);
  };

  const handleToggleFolder = async (id: string, enabled: boolean) => {
    await toggleFolder({ id: id as any, enabled });
  };

  const formatScanTime = (timestamp?: number) => {
    if (!timestamp) return "Never";
    return new Date(timestamp).toLocaleString();
  };

  return (
    <Box>
      <Stack sx={{ mb: 3 }}>
        <Typography variant="h4">Settings</Typography>
        <Typography variant="body2" color="text.secondary">
          Manage integrations, agent configuration, and system preferences
        </Typography>
      </Stack>

      {agentStatuses === undefined && <LinearProgress sx={{ mb: 2 }} />}

      <Grid container spacing={3}>
        {/* Section 0: SharePoint Folders (Scanning Configuration) */}
        <Grid item xs={12}>
          <Card
            sx={{
              background: "linear-gradient(135deg, rgba(0,120,212,0.08), rgba(0,120,212,0.02))",
              border: "1px solid rgba(0,120,212,0.15)",
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: 2,
                background: "linear-gradient(90deg, #0078d4, #00bcf2)",
              },
            }}
          >
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Stack>
                  <Typography variant="h6">SharePoint Folders</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Configure folders to scan for bank statements every 30 minutes
                  </Typography>
                </Stack>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  size="small"
                  onClick={() => setAddDialogOpen(true)}
                >
                  Add Folder
                </Button>
              </Stack>

              {scanFolders === undefined ? (
                <LinearProgress />
              ) : scanFolders.length === 0 ? (
                <Alert severity="info" variant="outlined">
                  No SharePoint folders configured. Click "Add Folder" to start scanning for bank statements.
                </Alert>
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ background: "transparent" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Folder Path</TableCell>
                        <TableCell>Site ID</TableCell>
                        <TableCell>File Types</TableCell>
                        <TableCell>Last Scan</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="center">Enabled</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {scanFolders.map((folder: any) => (
                        <TableRow key={folder._id}>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {folder.folderPath}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Tooltip title={folder.siteId}>
                              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {folder.siteId}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.5}>
                              {folder.fileTypes.map((ft: string) => (
                                <Chip key={ft} label={ft.toUpperCase()} size="small" variant="outlined" />
                              ))}
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {formatScanTime(folder.lastScanAt)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {folder.scanning ? (
                              <Chip label="Scanning..." color="primary" size="small" />
                            ) : folder.consecutiveFailures && folder.consecutiveFailures >= 3 ? (
                              <Tooltip title={folder.lastError || "Unknown error"}>
                                <Chip label="Error" color="error" size="small" />
                              </Tooltip>
                            ) : folder.lastScanAt ? (
                              <Chip label="OK" color="success" size="small" />
                            ) : (
                              <Chip label="Pending" color="default" size="small" />
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <Switch
                              checked={folder.enabled}
                              onChange={(e) => handleToggleFolder(folder._id, e.target.checked)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setDeleteConfirmId(folder._id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Section 1: QuickBooks Desktop Connection */}
        <Grid item xs={12} md={6}>
          <QBConnectionCard isAdmin={isAdmin} />
        </Grid>

        {/* Section 2: Email Configuration */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              background: "linear-gradient(135deg, rgba(116,185,255,0.08), rgba(116,185,255,0.02))",
              border: "1px solid rgba(116,185,255,0.15)",
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: 2,
                background: "linear-gradient(90deg, #74b9ff, #0984e3)",
              },
            }}
          >
            <CardContent>
              {(() => {
                // Map the rolled-up health from getStatus into the chip props.
                // "active" = agent online + processing, "idle" = online + idle,
                // "stale" = heartbeat 5–30min old, "offline" = >30min or marked
                // offline, "not_registered" = agent never heartbeated yet.
                const s = emailCollectionStatus;
                const chip: {
                  label: string;
                  color: "success" | "warning" | "error" | "default";
                } = (() => {
                  if (!s) return { label: "Loading…", color: "default" };
                  switch (s.health) {
                    case "active":
                      return { label: "Active · Processing", color: "success" };
                    case "idle":
                      return { label: "Active", color: "success" };
                    case "stale":
                      return { label: "Stale heartbeat", color: "warning" };
                    case "offline":
                      return { label: "Offline", color: "error" };
                    case "not_registered":
                    default:
                      return { label: "Not registered", color: "warning" };
                  }
                })();

                return (
                  <>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ mb: 2 }}
                    >
                      <Typography variant="h6">Email / Invoice Collection</Typography>
                      <Chip label={chip.label} color={chip.color} size="small" />
                    </Stack>

                    {!s ? (
                      <LinearProgress />
                    ) : (
                      <>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 2 }}
                        >
                          Microsoft Graph mail collector. Cron polls every{" "}
                          {s.cronIntervalMinutes} min and ingests attachments +
                          invoice-shaped email bodies via Agent 4.
                        </Typography>

                        <Stack spacing={1}>
                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="body2" color="text.secondary">
                              Mailbox
                            </Typography>
                            <Typography variant="body2">
                              {s.mailboxes.length > 0
                                ? s.mailboxes.join(", ")
                                : "Not yet observed"}
                            </Typography>
                          </Stack>
                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="body2" color="text.secondary">
                              Agent heartbeat
                            </Typography>
                            <Typography variant="body2">
                              {s.agent?.lastHeartbeat
                                ? `${timeAgo(s.agent.lastHeartbeat)}${s.agent.currentTask ? ` · ${s.agent.currentTask}` : ""}`
                                : "—"}
                            </Typography>
                          </Stack>
                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="body2" color="text.secondary">
                              Last email
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                maxWidth: 260,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                              title={
                                s.lastEmail
                                  ? `${s.lastEmail.subject ?? "(no subject)"} — ${s.lastEmail.fromAddress ?? ""}`
                                  : ""
                              }
                            >
                              {s.lastEmail
                                ? `${timeAgo(s.lastEmail.processedAt)} · ${s.lastEmail.fromAddress ?? "(unknown sender)"}`
                                : "None yet"}
                            </Typography>
                          </Stack>
                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="body2" color="text.secondary">
                              Last 24h
                            </Typography>
                            <Typography variant="body2">
                              {s.stats24h.emailsProcessed} email
                              {s.stats24h.emailsProcessed === 1 ? "" : "s"} ·{" "}
                              {s.stats24h.attachmentsIngested} attachment
                              {s.stats24h.attachmentsIngested === 1 ? "" : "s"}
                              {s.stats24h.duplicatesSkipped > 0
                                ? ` · ${s.stats24h.duplicatesSkipped} dup`
                                : ""}
                            </Typography>
                          </Stack>
                          {s.agent?.lastError && (
                            <Alert severity="error" sx={{ mt: 1 }}>
                              {s.agent.lastError}
                            </Alert>
                          )}
                        </Stack>
                      </>
                    )}
                  </>
                );
              })()}
            </CardContent>
          </Card>
        </Grid>

        {/* Section 3: Agent Connectivity */}
        <Grid item xs={12}>
          <Card
            sx={{
              background: "linear-gradient(135deg, rgba(0,206,201,0.06), rgba(85,239,196,0.02))",
              border: "1px solid rgba(0,206,201,0.12)",
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: 2,
                background: "linear-gradient(90deg, #00cec9, #55efc4)",
              },
            }}
          >
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h6">Agent System Status</Typography>
                {agentStatuses && agentStatuses.length > 0 && (
                  <Chip
                    label={`${agentStatuses.filter((a: any) => a.status !== "offline").length}/${agentStatuses.length} agents online`}
                    color="success"
                    size="small"
                  />
                )}
              </Stack>
              {agentStatuses === undefined ? (
                <LinearProgress />
              ) : agentStatuses.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No agents registered. Agents will appear after first startup.
                </Typography>
              ) : (
                <Grid container spacing={2}>
                  {agentStatuses.map((agent: any) => {
                    const c = agentColor(agent.agentId);
                    const { name, role } = splitAgentName(agent.agentName);
                    return (
                    <Grid item xs={12} sm={6} md={4} key={agent._id}>
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 2.5,
                          background: `linear-gradient(135deg, ${tint(c, 0.14)} 0%, ${tint(c, 0.04)} 100%)`,
                          border: `1px solid ${tint(c, 0.35)}`,
                          borderLeft: `4px solid ${c}`,
                          transition: "border-color 0.2s ease, transform 0.2s ease",
                          "&:hover": {
                            transform: "translateY(-2px)",
                          },
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={1.25}>
                          <AgentAvatar agentId={agent.agentId} size={36} />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.1 }}>{name}</Typography>
                            {role && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontSize: "0.65rem" }}>
                                {role}
                              </Typography>
                            )}
                          </Box>
                          <Chip
                            size="small"
                            label={agent.status}
                            color={
                              agent.status === "idle" ? "success"
                              : agent.status === "processing" ? "primary"
                              : agent.status === "error" ? "error"
                              : "default"
                            }
                          />
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          Last heartbeat: {timeAgo(agent.lastHeartbeat)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Completed: {agent.tasksCompleted} | Failed: {agent.tasksFailed}
                        </Typography>
                      </Box>
                    </Grid>
                    );
                  })}
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Section 4: System Information */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              background: "linear-gradient(135deg, rgba(108,92,231,0.06), rgba(162,155,254,0.02))",
              border: "1px solid rgba(108,92,231,0.12)",
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: 2,
                background: "linear-gradient(90deg, #6c5ce7, #a29bfe)",
              },
            }}
          >
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>System Information</Typography>
              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">Application Version</Typography>
                  <Typography variant="body2">1.0.0</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">Environment</Typography>
                  <Typography variant="body2">Development</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">Backend</Typography>
                  <Typography variant="body2">Convex</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">Frontend</Typography>
                  <Typography variant="body2">React + Vite + MUI</Typography>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Section 5: User Management */}
        <Grid item xs={12}>
          <Card
            sx={{
              background: "linear-gradient(135deg, rgba(0,184,148,0.06), rgba(85,239,196,0.02))",
              border: "1px solid rgba(0,184,148,0.12)",
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: 2,
                background: "linear-gradient(90deg, #00b894, #55efc4)",
              },
            }}
          >
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Stack>
                  <Typography variant="h6">User Management</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Manage user roles — Admin, Accountant, Viewer
                  </Typography>
                </Stack>
                {isAdmin && (
                  <Button
                    startIcon={<Add />}
                    variant="contained"
                    size="small"
                    onClick={() => setAddUserOpen(true)}
                    sx={{
                      background: "linear-gradient(135deg, #00b894, #00cec9)",
                      textTransform: "none",
                      fontWeight: 600,
                      "&:hover": { background: "linear-gradient(135deg, #00a884, #00beb9)" },
                    }}
                  >
                    Add User
                  </Button>
                )}
              </Stack>

              {!isAdmin ? (
                <Alert severity="info" variant="outlined">
                  Only administrators can manage users.
                </Alert>
              ) : allUsers === undefined ? (
                <LinearProgress />
              ) : allUsers.length === 0 ? (
                <Alert severity="info" variant="outlined">
                  No users configured. Click "Add User" to create the first user.
                </Alert>
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ background: "transparent" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Role</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {allUsers.map((user: any) => (
                        <TableRow key={user._id}>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {user.name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8rem" }}>
                              {user.email}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                              size="small"
                              color={user.role === "admin" ? "primary" : user.role === "accountant" ? "warning" : "default"}
                              variant={user.role === "admin" ? "filled" : "outlined"}
                              sx={{ height: 24, fontSize: "0.7rem" }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                              <FormControl size="small" sx={{ minWidth: 110 }}>
                                <Select
                                  value={user.role}
                                  onChange={(e) => {
                                    upsertUser({ email: user.email, role: e.target.value as any });
                                  }}
                                  sx={{ fontSize: "0.75rem", height: 30 }}
                                >
                                  <MenuItem value="admin" sx={{ fontSize: "0.8rem" }}>Admin</MenuItem>
                                  <MenuItem value="accountant" sx={{ fontSize: "0.8rem" }}>Accountant</MenuItem>
                                  <MenuItem value="viewer" sx={{ fontSize: "0.8rem" }}>Viewer</MenuItem>
                                </Select>
                              </FormControl>
                              {user.email !== email.toLowerCase() && (
                                <IconButton
                                  size="small"
                                  onClick={() => setDeleteUserId(user._id)}
                                  sx={{ color: "var(--c-text-muted)", "&:hover": { color: "#ff7675" } }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* QB Desktop Command Queue */}
      <Box sx={{ mt: 4 }}>
        <QBCommandQueue />
      </Box>

      {/* Add Folder Dialog — 3-step wizard */}
      <Dialog open={addDialogOpen} onClose={handleCloseAddDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            {wizardStep > 1 && (
              <IconButton
                size="small"
                onClick={() => {
                  if (wizardStep === 3) {
                    setWizardStep(2);
                    setSelectedDrive(null);
                    setFolders([]);
                    setBreadcrumb([]);
                  } else if (wizardStep === 2) {
                    setWizardStep(1);
                    setSiteInfo(null);
                    setDrives([]);
                  }
                  setWizardError(null);
                }}
              >
                <ArrowBack fontSize="small" />
              </IconButton>
            )}
            <Typography variant="h6" component="span">
              {wizardStep === 1 && "Step 1: Connect to SharePoint Site"}
              {wizardStep === 2 && "Step 2: Select Document Library"}
              {wizardStep === 3 && "Step 3: Browse & Select Folder"}
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {wizardError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setWizardError(null)}>
              {wizardError}
            </Alert>
          )}

          {/* Step 1: Enter SharePoint URL */}
          {wizardStep === 1 && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Enter your SharePoint site URL to get started.
              </Typography>
              <TextField
                label="SharePoint Site URL"
                placeholder="https://altelix.sharepoint.com/sites/Finance"
                value={siteUrl}
                onChange={(e) => setSiteUrl(e.target.value)}
                fullWidth
                disabled={loading}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleResolveSite();
                }}
              />
              <Button
                variant="contained"
                onClick={handleResolveSite}
                disabled={!siteUrl.trim() || loading}
                startIcon={loading ? <CircularProgress size={18} /> : undefined}
              >
                {loading ? "Connecting..." : "Connect"}
              </Button>
            </Stack>
          )}

          {/* Step 2: Select Document Library */}
          {wizardStep === 2 && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Alert severity="success" variant="outlined" icon={false} sx={{ py: 0.5 }}>
                <Typography variant="body2">
                  Connected to <strong>{siteInfo?.displayName}</strong>
                </Typography>
              </Alert>
              <Typography variant="body2" color="text.secondary">
                Select a document library:
              </Typography>
              {loading ? (
                <Stack alignItems="center" sx={{ py: 3 }}>
                  <CircularProgress size={28} />
                </Stack>
              ) : drives.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No document libraries found on this site.
                </Typography>
              ) : (
                <List disablePadding sx={{ border: "1px solid var(--c-overlay-8)", borderRadius: 1 }}>
                  {drives.map((drive, idx) => (
                    <ListItemButton
                      key={drive.driveId}
                      onClick={() => handleSelectDrive(drive)}
                      divider={idx < drives.length - 1}
                      sx={{ "&:hover": { bgcolor: "rgba(0,120,212,0.08)" } }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <FolderOutlined fontSize="small" sx={{ color: "primary.main" }} />
                      </ListItemIcon>
                      <ListItemText primary={drive.name} />
                      <NavigateNext fontSize="small" sx={{ color: "text.secondary" }} />
                    </ListItemButton>
                  ))}
                </List>
              )}
            </Stack>
          )}

          {/* Step 3: Browse & Select Folder */}
          {wizardStep === 3 && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              {/* Breadcrumb navigation */}
              <Breadcrumbs separator={<NavigateNext fontSize="small" />} sx={{ fontSize: "0.875rem" }}>
                {breadcrumb.map((crumb, idx) => {
                  const isLast = idx === breadcrumb.length - 1;
                  return isLast ? (
                    <Typography key={idx} variant="body2" color="text.primary" fontWeight={500}>
                      {crumb.name}
                    </Typography>
                  ) : (
                    <Link
                      key={idx}
                      component="button"
                      variant="body2"
                      underline="hover"
                      color="primary"
                      onClick={() => handleBreadcrumbClick(idx)}
                      sx={{ cursor: "pointer" }}
                    >
                      {crumb.name}
                    </Link>
                  );
                })}
              </Breadcrumbs>

              {/* Select this folder button */}
              <Button
                variant="contained"
                size="small"
                onClick={handleSelectThisFolder}
                sx={{ alignSelf: "flex-start" }}
              >
                Select This Folder
              </Button>

              {/* File types selector */}
              <FormControl fullWidth size="small">
                <InputLabel>File Types</InputLabel>
                <Select
                  multiple
                  value={selectedFileTypes}
                  onChange={(e) => setSelectedFileTypes(e.target.value as string[])}
                  input={<OutlinedInput label="File Types" />}
                  renderValue={(selected) => (selected as string[]).map((s) => s.toUpperCase()).join(", ")}
                >
                  {["pdf", "csv", "ofx"].map((ft) => (
                    <MenuItem key={ft} value={ft}>
                      {ft.toUpperCase()}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Folder list */}
              {loading ? (
                <Stack alignItems="center" sx={{ py: 3 }}>
                  <CircularProgress size={28} />
                </Stack>
              ) : folders.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
                  No subfolders in this location. Use "Select This Folder" to add the current folder.
                </Typography>
              ) : (
                <List disablePadding sx={{ border: "1px solid var(--c-overlay-8)", borderRadius: 1 }}>
                  {folders.map((folder, idx) => (
                    <ListItemButton
                      key={folder.id}
                      onClick={() => handleNavigateFolder(folder.id, folder.name)}
                      divider={idx < folders.length - 1}
                      sx={{ "&:hover": { bgcolor: "rgba(0,120,212,0.08)" } }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <FolderOutlined fontSize="small" sx={{ color: "primary.main" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={folder.name}
                        secondary={folder.childCount > 0 ? `${folder.childCount} items` : undefined}
                      />
                      <NavigateNext fontSize="small" sx={{ color: "text.secondary" }} />
                    </ListItemButton>
                  ))}
                </List>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Folder Confirmation Dialog */}
      <Dialog open={!!deleteConfirmId} onClose={() => setDeleteConfirmId(null)}>
        <DialogTitle>Remove Folder</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to remove this folder from scanning? This will not delete any already-downloaded files.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmId(null)}>Cancel</Button>
          <Button
            onClick={() => deleteConfirmId && handleRemoveFolder(deleteConfirmId)}
            color="error"
            variant="contained"
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add User Dialog */}
      <Dialog
        open={addUserOpen}
        onClose={() => { setAddUserOpen(false); setNewUserEmail(""); setNewUserName(""); setNewUserRole("viewer"); }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Add User</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Email"
              placeholder="user@altelix.onmicrosoft.com"
              value={newUserEmail}
              onChange={(e) => setNewUserEmail(e.target.value)}
              fullWidth
              required
              type="email"
            />
            <TextField
              label="Display Name"
              placeholder="John Doe"
              value={newUserName}
              onChange={(e) => setNewUserName(e.target.value)}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={newUserRole}
                onChange={(e) => setNewUserRole(e.target.value as any)}
                label="Role"
              >
                <MenuItem value="admin">Admin — Full system access</MenuItem>
                <MenuItem value="accountant">Accountant — Transaction review, matching</MenuItem>
                <MenuItem value="viewer">Viewer — Read-only dashboard</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setAddUserOpen(false); setNewUserEmail(""); setNewUserName(""); setNewUserRole("viewer"); }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!newUserEmail}
            onClick={async () => {
              await upsertUser({ email: newUserEmail, name: newUserName || undefined, role: newUserRole });
              setAddUserOpen(false);
              setNewUserEmail("");
              setNewUserName("");
              setNewUserRole("viewer");
            }}
            sx={{
              background: "linear-gradient(135deg, #00b894, #00cec9)",
              "&:hover": { background: "linear-gradient(135deg, #00a884, #00beb9)" },
            }}
          >
            Add User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete User Confirmation Dialog */}
      <Dialog open={!!deleteUserId} onClose={() => setDeleteUserId(null)}>
        <DialogTitle>Remove User</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to remove this user? They will lose all assigned roles and revert to default viewer access.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteUserId(null)}>Cancel</Button>
          <Button
            onClick={async () => {
              if (deleteUserId) {
                await removeUser({ id: deleteUserId as any });
                setDeleteUserId(null);
              }
            }}
            color="error"
            variant="contained"
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
